import { useCurrentAccount } from "@mysten/dapp-kit-react";
import { first } from "lodash";
import { useEffect, useMemo, useState } from "react";

import { config } from "@/config";
import { useSuiQueryContext } from "@/context/SUIQuery/useSuiQueryContext";
import { useSuiObject, useSuiOwnedObjects } from "@/hooks/sui/queries";

import { OwnedReferrerProps } from "./OwnedReferrer.types";

const REFERRER_RECEIPT_TYPE = `${config.packageId}::swarm_license_sale::ReferrerReceipt`;

const BASE_COIN_UNIT = 1_000_000;
const BASE_REFERRAL_RATE = 50_000; // 5% of USDC 6 decimal units 50000/1000000
const REFERRAL_RATE_INCREMENT = 2_000; // 2% of USDC 6 decimal units 20000/1000000
const MAX_REFERRAL_RATE = 100_000; // 10% of USDC 6 decimal units
const REFERRALS_PER_TIER = 5;

const calculateReferralCashbackRate = (sales: number) => {
  if (sales === 0) {
    return { tier: 1, rate: BASE_REFERRAL_RATE / BASE_COIN_UNIT };
  }

  // Calculate tier based on ranges:
  // Tier 0 (base): 1-5 referrals
  // Tier 1: 6-10 referrals
  // Tier 2: 11-15 referrals
  // And so on...
  const tier = Math.floor((sales - 1) / REFERRALS_PER_TIER);

  // Calculate rate with 2% increment per tier, using 6 decimal precision
  const rate = Math.min(BASE_REFERRAL_RATE + tier * REFERRAL_RATE_INCREMENT, MAX_REFERRAL_RATE);

  return {
    rate: rate / BASE_COIN_UNIT,
    tier,
  };
};

export const OwnedReferrer: React.FC<OwnedReferrerProps> = () => {
  const [referrerReceipt, setReferrerReceipt] = useState<{ referrerId: string } | undefined>(
    undefined,
  );

  const account = useCurrentAccount();

  const { setOwnedReferrer, transactionOutput } = useSuiQueryContext();

  const { data, isPending, error, refetch } = useSuiOwnedObjects({
    owner: account?.address,
    type: REFERRER_RECEIPT_TYPE,
  });

  const { data: referrerObjectData, refetch: refetchReferrerObjectData } = useSuiObject(
    referrerReceipt?.referrerId,
  );

  const ownedReferrerReceiptFields = useMemo(() => {
    if (!data?.objects || data.objects.length === 0) return undefined;

    // MIGRATION: flat JSON contents — `json.referrer_id`, not
    // `data.content.fields.referrer_id`.
    const fields = first(data.objects)!.json as unknown as {
      referrer_id: string;
    } | null;

    if (!fields) return undefined;

    return {
      referrerId: fields.referrer_id,
    };
  }, [data?.objects, account?.address]);

  const referrerData = useMemo(() => {
    if (!referrerObjectData?.json) return undefined;

    const fields = referrerObjectData.json as unknown as {
      referrals: { size: string };
      code: string;
      sales: string;
    } | null;

    if (!fields) return undefined;

    const referralsCount = fields.referrals?.size;
    const code = fields.code;
    const sales = fields.sales;

    if (!referralsCount) return undefined;

    const { tier, rate } = calculateReferralCashbackRate(Number(sales));

    const maxReferralsPerTier = Math.floor(1 / (REFERRAL_RATE_INCREMENT / MAX_REFERRAL_RATE) / 2);

    return {
      sales,
      code,
      referralsCount,
      rebate: `${(rate * 100).toFixed(2)}%`,
      rebateLevel: {
        value: tier,
        text: `${tier}/${maxReferralsPerTier}`,
        maxReferralsPerTier,
      },
    };
  }, [referrerObjectData?.json]);

  useEffect(() => {
    if (!referrerData) {
      setOwnedReferrer({
        sales: "0",
        referralsCount: "0",
        rebate: "",
        rebateLevel: {
          value: 0,
          text: "0/50",
          maxReferralsPerTier: 0,
        },
      });

      return;
    }

    setOwnedReferrer(referrerData);
  }, [referrerData]);

  useEffect(() => {
    if (!ownedReferrerReceiptFields?.referrerId) {
      setReferrerReceipt(undefined);

      return;
    }

    setReferrerReceipt(ownedReferrerReceiptFields);
  }, [ownedReferrerReceiptFields]);

  useEffect(() => {
    refetch();
  }, [account?.address, transactionOutput.purchaseLicense.onSuccess?.digest]);

  useEffect(() => {
    refetchReferrerObjectData();
  }, [account?.address]);

  if (error) {
    return null;
  }

  if (isPending) {
    return null;
  }

  return null;
};
